import { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ChangeList, ChangeListRow, ChangeSummary, DefinitionList, IdentityAvatar, SearchField, SectionCard } from '../../../design-system';
import type { PeopleWorker } from '../operations/model';

export type StaffingChangePreview = {
  worker: PeopleWorker;
  changes: Array<{ label: string; before: string; after: string }>;
};

export function PeopleBulkChangeReview({ preview, date, reason, asOf }: {
  preview: StaffingChangePreview[];
  date: string;
  reason: string;
  asOf: string;
}) {
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(20);
  const search = query.trim().toLocaleLowerCase();
  const filtered = preview.filter(({ worker, changes }) => [worker.name, worker.organization, worker.jobProfile, worker.title,
    ...changes.flatMap(({ label, before, after }) => [label, before, after])].some((value) => value?.toLocaleLowerCase().includes(search)));
  return <Stack component="section" aria-label="일괄 발령 변경 내용" spacing={2}>
    <SectionCard title="기본 정보" showDivider={false}>
      <Box sx={{ px: 2, pb: 2 }}><DefinitionList items={[
        ['시행일', date],
        ['발령 사유', <Box sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{reason}</Box>],
        ['변경 전 값 기준일', asOf],
      ]} /></Box>
    </SectionCard>
    <SectionCard title={`구성원 발령 ${preview.length}명`} showDivider={false}>
    <Stack spacing={2} sx={{ px: 2, pb: 2 }}>
    {preview.length > 5 && <SearchField label="변경 내용 검색" placeholder="이름·조직·변경 내용 검색" value={query} onValueChange={(value) => { setQuery(value); setLimit(20); }} />}
    {search && <Typography role="status" variant="caption" color="text.secondary">검색 결과 {filtered.length}명 · 발령 대상 {preview.length}명</Typography>}
    {filtered.length > 0 ? <ChangeList label="구성원별 발령 내용">
      {filtered.slice(0, limit).map(({ worker, changes }) => <ChangeListRow key={worker.id} ariaLabel={`${worker.name} 변경 확인`} identity={
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
          <IdentityAvatar size={40} src={worker.photoUrl} alt="" aria-hidden="true">{worker.initials}</IdentityAvatar>
          <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{worker.name}</Typography>
            {(worker.jobProfile || worker.title) && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{worker.jobProfile || worker.title}</Typography>}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{worker.organization || '소속 미설정'}</Typography>
          </Box>
        </Stack>
        }>
          <ChangeSummary layout="inline" emptyValue="없음" items={changes} />
      </ChangeListRow>)}
    </ChangeList> : <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>검색 결과가 없습니다.</Typography>}
    {filtered.length > limit && <Button variant="outlined" sx={{ alignSelf: 'center' }} onClick={() => setLimit((value) => value + 20)}>더 보기 ({Math.min(limit, filtered.length)}/{filtered.length}명)</Button>}
    </Stack>
    </SectionCard>
  </Stack>;
}
